const READY_STATES = {
  CONNECT_ALPACA: {
    label: 'CONNECT ALPACA',
    tone: 'amber',
    detail: 'Connect or reconnect Alpaca Paper so this bot can receive market data.',
    needsAttention: true,
    reconnect: true,
  },
  START_REQUESTED: {
    label: 'START REQUESTED',
    tone: 'blue',
    detail: 'Pinngro is starting the bot. Its first successful market check is not yet confirmed.',
  },
  PREPARING: {
    label: 'PREPARING',
    tone: 'blue',
    detail: 'The bot worker is starting. Pinngro will confirm when its first market check succeeds.',
  },
  WAITING_FOR_MARKET_DATA: {
    label: 'WAITING FOR MARKET DATA',
    tone: 'amber',
    detail: 'A market check is retrying safely. No suggestion or transaction was created.',
  },
  WAITING_FOR_NEXT_COMPLETED_SESSION: {
    label: 'READY · MARKET CLOSED',
    tone: 'blue',
    detail: 'The bot is configured. Its first check will use the next supported completed market session.',
  },
  FIRST_MARKET_CHECK_PENDING: {
    label: 'FIRST MARKET CHECK PENDING',
    tone: 'blue',
    detail: 'The bot is configured and its first successful evaluation is still pending.',
  },
  WATCHING: {
    label: 'WATCHING',
    tone: 'emerald',
    detail: 'A successful completed-data market check has been recorded.',
  },
  NEEDS_ATTENTION: {
    label: 'NEEDS ATTENTION',
    tone: 'amber',
    detail: 'Market checks need attention. Nothing executed.',
    needsAttention: true,
  },
};

const REASON_PRESENTATION = {
  alpaca_connection_required: {
    label: 'CONNECT ALPACA', tone: 'amber', reconnect: true, needsAttention: true,
    detail: 'Reconnect Alpaca Paper so Pinngro can receive market data.',
  },
  evaluation_not_succeeding: {
    label: 'EVALUATION OVERDUE', tone: 'amber', needsAttention: true,
    detail: 'Market checks have not completed successfully on schedule. Pinngro is still connected to Alpaca, but this bot’s scheduled evaluation is not completing.',
  },
  pinngro_market_check_problem: {
    label: 'MARKET CHECK NEEDS SUPPORT', tone: 'amber', needsAttention: true,
    detail: 'Pinngro could not finish this bot’s market-data check. Your bot is still set to watch, but no successful evaluation was recorded. Pinngro will try again automatically; support needs to investigate the repeated failure.',
  },
  market_evidence_not_current: {
    label: 'MARKET EVIDENCE STALE', tone: 'amber', needsAttention: true,
    detail: 'Alpaca is connected, but the latest completed market-data evidence is not current enough for this bot to evaluate safely.',
  },
  market_check_retrying: {
    label: 'RETRYING MARKET DATA', tone: 'amber',
    detail: 'Pinngro is retrying this bot’s market-data check. Nothing executed.',
  },
  worker_starting: {
    label: 'WORKER STARTING', tone: 'blue',
    detail: 'The bot worker is starting. Its first successful market check has not completed yet.',
  },
  first_check_in_progress: {
    label: 'FIRST CHECK PENDING', tone: 'blue',
    detail: 'The bot is configured and its first market check is still pending.',
  },
  market_closed_or_non_session: {
    label: 'MARKET CLOSED', tone: 'blue',
    detail: 'The bot is waiting for the next supported completed market session.',
  },
  evaluation_succeeded: {
    label: 'WATCHING', tone: 'emerald',
    detail: 'A successful completed-data market check has been recorded.',
  },
};

export const presentBotReadiness = (bot, runtime) => {
  if (bot?.status === 'draft') {
    const preparation = bot.market_data_preparation;
    const base = {
      preparationStatus: preparation?.status || null,
      failureSymbols: preparation?.failure_symbols || [],
      retryAvailable: preparation?.retry_available === true,
    };
    if (preparation?.status === 'PENDING') {
      return {
        ...base,
        label: 'PREPARATION REQUESTED', tone: 'blue',
        detail: 'Market-history preparation is queued. Nothing has started.',
      };
    }
    if (preparation?.status === 'RUNNING') {
      return {
        ...base,
        label: 'PREPARING MARKET DATA', tone: 'blue',
        detail: 'Pinngro is securely preparing the completed market history this strategy needs.',
      };
    }
    if (preparation?.status === 'NEEDS_ALPACA_RECONNECT') {
      return {
        ...base,
        label: 'CONNECT ALPACA', tone: 'amber', reconnect: true, needsAttention: true,
        detail: 'Connect or reconnect Alpaca Paper, then retry market-data preparation.',
      };
    }
    if (preparation?.status === 'SYMBOL_UNSUPPORTED') {
      return {
        ...base,
        label: 'STOCK NOT SUPPORTED', tone: 'amber', needsAttention: true,
        detail: 'One or more selected stocks cannot use this bot’s market-data contract.',
      };
    }
    if (preparation?.status === 'INSUFFICIENT_HISTORY') {
      return {
        ...base,
        label: 'NOT ENOUGH HISTORY', tone: 'amber', needsAttention: true,
        detail: 'One or more selected stocks do not yet have enough valid completed history.',
      };
    }
    if (['PROVIDER_UNAVAILABLE', 'PREPARATION_FAILED'].includes(preparation?.status)) {
      return {
        ...base,
        label: 'PREPARATION NEEDS ATTENTION', tone: 'amber', needsAttention: true,
        detail: 'Market-data preparation did not complete. Nothing started; retry when ready.',
      };
    }
    return { label: 'CREATED', tone: 'slate', detail: 'Saved, but not prepared or started.' };
  }
  if (runtime?.error) {
    return {
      label: 'NEEDS ATTENTION', tone: 'amber',
      detail: 'Pinngro could not verify this bot’s current Practice status.',
      needsAttention: true,
    };
  }
  if (runtime?.desired_state === 'pause') {
    return { label: 'PAUSED', tone: 'amber', detail: 'Market checks are paused until you resume.' };
  }
  if (runtime?.desired_state === 'stop') {
    return bot?.lifecycle?.dry_run_complete
      ? { label: 'ENDED', tone: 'slate', detail: 'Pinngro Practice checks have ended.' }
      : { label: 'READY', tone: 'blue', detail: 'Ready for you to start Practice checks.' };
  }
  if (runtime?.desired_state === 'run') {
    const evidence = runtime.market_watch;
    if (!evidence) {
      return {
        label: 'FIRST MARKET CHECK PENDING', tone: 'blue',
        detail: 'Start was requested; successful market evaluation is not yet confirmed.',
      };
    }
    const evidenceState = evidence.state === 'WATCHING' && (
      !evidence.last_successful_evaluation_at
      || evidence.snapshot_evidence_valid !== true
    )
      ? 'NEEDS_ATTENTION'
      : evidence.state;
    const reasonPresentation = REASON_PRESENTATION[evidence.reason] || {};
    return {
      ...(READY_STATES[evidenceState] || READY_STATES.NEEDS_ATTENTION),
      ...reasonPresentation,
      reason: evidence.reason || null,
      marketDataAccessState: evidence.market_data_access_state || null,
      lastAttemptedCheck: evidence.last_evaluation_attempt_at || null,
      lastSuccessfulCheck: evidence.last_successful_evaluation_at || null,
      latestMarketDataAt: evidence.latest_market_data_at || null,
      snapshotEvidenceValid: evidence.snapshot_evidence_valid === true,
      consecutiveFailures: Number(evidence.consecutive_evaluation_failures || 0),
      nextExpectedSessionDate: evidence.next_expected_session_date || null,
      marketSession: evidence.market_session || null,
      workerLeaseHealthy: evidence.worker_lease_healthy === true,
      safeFailureCategory: evidence.safe_failure_category || null,
      nextAutomaticRetryEarliestAt: evidence.next_automatic_retry_earliest_at || null,
      requiredAdjustmentBasis: evidence.required_adjustment_basis || 'raw',
    };
  }
  return { label: 'READY', tone: 'blue', detail: 'Ready for you to start Pinngro Practice.' };
};

export const isTruthfullyWatching = (bot, runtime, presentedReadiness = null) => (
  (presentedReadiness || presentBotReadiness(bot, runtime)).label === 'WATCHING'
);
